import { array, number, object, string } from "yup";
import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import Validations from "@helpers/validations";
import useAxios from "@hooks/use-axios";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    name            : "",
    description     : "",
    life_cycle_start: [{ condition: "attribute", name: "", operator: "", value: "" }],
    life_cycle_end  : [{ condition: "attribute", name: "", operator: "", value: "" }],
    min_day         : 0,
    max_day         : 0,
    onTrackCount    : 0,
    stuckCount      : 0,
    behindCount     : 0
};

const useUpsert = props => {
    const {
        handleModal,
        getLifecycle,
        isEdit,
        modalConfig: { row }
    } = props;
    const { platformId } = useParams();  
    const { loading, axiosInstance } = useAxios({
        base         : "panel",
        initialLoader: isEdit
    });
    const { loading: updateLoading, axiosInstance: upsertAxiosInstance } =
        useAxios({
            base         : "panel",
            initialLoader: isEdit
        });

    const { requiredValidation, numberValidation } = Validations();
    const validationSchema = object().shape({
        name            : requiredValidation({ name: "Name" }),
        description     : requiredValidation({ name: "Description" }),
        life_cycle_start: array()
            .of(
                object().shape({
                    condition: requiredValidation({ name: "Condition" }),
                    name     : string().when("condition", {
                        is       : val => val !== "manually_start",
                        then     : () => requiredValidation({ name: "Name" }),
                        otherwise: () => string()
                    }),

                    operator: string().when("condition", {
                        is       : val => val === "attribute",
                        then     : () => requiredValidation({ name: "Operator" }),
                        otherwise: () => string()
                    }),

                    value: string().when("condition", {
                        is       : val => val !== "manually_start",
                        then     : () => requiredValidation({ name: "Value" }),
                        otherwise: () => string()
                    })
                })
            )
            .min(1, "At least one start condition is required"),

        life_cycle_end: array()
            .of(
                object().shape({
                    condition: requiredValidation({ name: "Condition" }),
                    name     : requiredValidation({ name: "Name" }),

                    operator: string().when("condition", {
                        is       : val => val === "attribute",
                        then     : () => requiredValidation({ name: "Operator" }),
                        otherwise: () => string()
                    }),

                    value: requiredValidation({ name: "Value" })
                })
            )
            .min(1, "At least one condition is required"),

        min_day     : numberValidation({ name: "Minimum Day", min: 1, max: 365 }),
        max_day     : numberValidation({ name: "Maximum Day", min: 1, max: 365 }),
        onTrackCount: number().optional(),
        stuckCount  : number().optional(),
        behindCount : number().optional()

    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });
    const [goals, setGoals] = useState([]);

    const getLifecycleDetails = async () => {
        const { data } = await axiosInstance.get(`/lifecycle/${platformId}/${row._id}`);
        reset({
            name            : data.name,
            description     : data.description,
            life_cycle_start: data.lifecycleStart,
            life_cycle_end  : data.lifecycleEnd,
            min_day         : data.minDays,
            max_day         : data.maxDays,
            onTrackCount    : data.onTrackCount,
            stuckCount      : data.stuckCount,
            behindCount     : data.behindCount
        });
    };

    const handleGoals = async data => {
        setGoals(data);
    };
    useEffect(() => {
        let isActive = true;
        if (isEdit && row._id && isActive) {
            getLifecycleDetails();
        } else {
            reset(defaultValues);
            handleGoals([]);
        }
        return () => {
            isActive = false;
        };
    }, [isEdit, row]);

    const onSubmit = () => {
        handleSubmit(async data => {
            const payload = {
                name          : data.name,
                description   : data.description,
                lifecycleStart: data.life_cycle_start.map(item => ({
                    condition: item.condition,
                    name     : item.name,
                    operator : item.operator,
                    value    : item.operator ? Number(item.value) : item.value
                })),
                lifecycleEnd: data.life_cycle_end.map(item => {
                    if (item.condition !== "attribute") {
                        return {
                            condition: item.condition,
                            name     : item.name,
                            value    : item.value
                        };
                    }
                    return {
                        condition: item.condition,
                        name     : item.name,
                        operator : item.operator,
                        value    : item.operator ? Number(item.value) : item.value
                    };
                }),
                minDays: Number(data.min_day),
                maxDays: Number(data.max_day),
                ...(!isEdit && {
                    goals: goals.map(item => ({
                        conditions: item.conditions,
                        name      : item.name
                    }))
                })
            };

            if (row._id) {
                await upsertAxiosInstance.patch(
                    `/lifecycle/${platformId}/${row._id}`,
                    payload
                );
            } else {
                await upsertAxiosInstance.post(`/lifecycle/${platformId}`, payload);
            }
            handleModal();
            getLifecycle();
        })();
    };

    return {
        onSubmit,
        methods,
        loading,
        isDirty,
        handleGoals,
        goals,
        updateLoading
    };
};

export default useUpsert;
